// client/src/coms/components/PackInfoDialog.js
// Sticker pack information page, opened from a share code / pack ID (Settings, server settings) or from the
// Marketplace. Shows what the pack is and what the viewer may do with it; the server decides every action
// (GET /stickers/packs/:id, /stickers/lookup, /stickers/by-code/:code all return `pack` with the actions).
import React, { useState } from 'react';
import { comsApiPost } from '../comsApi';
import { IMPORT_WARNING } from './TrustDialog';
import { ModalShell } from './StickerEditorModal';
import { notifyStickersChanged } from '../stickers/stickerSuggest';
import UserAvatar from '../views/games_parts/UserAvatar';

function errText(e, fallback) {
  return String((e && e.data && e.data.error) || (e && e.message) || fallback);
}

export function formatPrice(price) {
  if (!price) return 'Free';
  try {
    return new Intl.NumberFormat(undefined, { style: 'currency', currency: price.currency }).format(price.amount_minor / 100);
  } catch {
    return `${(price.amount_minor / 100).toFixed(2)} ${price.currency}`;
  }
}

export const VISIBILITY_LABEL = {
  public: 'Public',
  unlisted: 'Unlisted',
  friends: 'Friends',
  private: 'Private',
  server: 'Server pack',
  local: 'Local',
};

export function PackIcon({ pack, size = 48 }) {
  const src = pack.icon_url || (pack.stickers && pack.stickers[0] && pack.stickers[0].url) || '';
  return (
    <div style={{ width: size, height: size, borderRadius: Math.round(size / 4), background: 'rgba(var(--vx-fg-rgb, 255,255,255), calc(0.05 * var(--vx-ov, 1)))', flex: '0 0 auto', display: 'flex', alignItems: 'center', justifyContent: 'center', overflow: 'hidden' }}>
      {src ? <img src={src} alt="" style={{ width: '90%', height: '90%', objectFit: 'contain' }} /> : <span style={{ fontSize: Math.round(size / 2.4) }}>🏷️</span>}
    </div>
  );
}

// pack: detail from the server. serverKey: when opened from a server's settings, enabling targets it.
export default function PackInfoDialog({ pack, onClose, onChanged, serverKey = '' }) {
  const [busy, setBusy] = useState('');
  const [err, setErr] = useState('');
  const [done, setDone] = useState('');
  const [trusted, setTrusted] = useState(false);
  const [state, setState] = useState(pack);
  const servers = (state.servers || []).filter((s) => !serverKey || s.key === serverKey);

  async function act(kind, fn, message) {
    setBusy(kind);
    setErr('');
    try {
      await fn();
      setDone(message);
      notifyStickersChanged();
      if (typeof onChanged === 'function') onChanged(kind.startsWith('enable') ? 'enable' : kind);
    } catch (e) {
      setErr(errText(e, 'That did not work.'));
    } finally {
      setBusy('');
    }
  }

  const install = () =>
    act('install', async () => {
      await comsApiPost(`/stickers/packs/${state.id}/install`, { consent: true });
      setState((s) => ({ ...s, installed: true, can_install: false }));
    }, 'Added to your stickers. You can use it in DMs and in servers that allow external stickers.');

  const enable = (key) =>
    act(`enable:${key}`, async () => {
      await comsApiPost(`/stickers/servers/${encodeURIComponent(key)}/packs`, { pack_id: state.id });
      setState((s) => ({ ...s, servers: (s.servers || []).map((x) => (x.key === key ? { ...x, enabled: true, can_enable: false } : x)) }));
    }, 'Enabled for the server. Every member can use it there.');

  const [checkout, setCheckout] = useState(null); // { quote, message } when payments are not active
  const buy = () =>
    act('buy', async () => {
      try {
        const r = await comsApiPost(`/stickers/packs/${state.id}/checkout`, {});
        if (r && r.checkout_url) window.location.assign(r.checkout_url);
      } catch (e) {
        if (e && e.data && e.data.code === 'payments_not_configured') {
          setCheckout({ quote: e.data.quote, message: e.data.error });
          return;
        }
        throw e;
      }
    }, '');

  const needsTrust = !state.is_mine;
  const paid = !state.is_free;

  return (
    <ModalShell label={`Sticker pack ${state.name}`} onClose={() => !busy && onClose()} width={560}>
      <div style={{ display: 'flex', gap: 14, alignItems: 'flex-start' }}>
        <PackIcon pack={state} size={64} />
        <div style={{ minWidth: 0, flex: 1 }}>
          <div style={{ fontSize: 18, fontWeight: 900, overflowWrap: 'anywhere' }}>{state.name}</div>
          <div style={{ fontSize: 12, opacity: 0.75, marginTop: 4, display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
            {state.owner_user_id ? <UserAvatar userId={state.owner_user_id} name={state.creator || '?'} size={18} /> : null}
            <span>by {state.creator || 'someone'} · {state.sticker_count} sticker{state.sticker_count === 1 ? '' : 's'}{state.install_count ? ` · ${state.install_count} ${state.install_count === 1 ? 'person has' : 'people have'} it` : ''} · ID {state.id}</span>
          </div>
          <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginTop: 6 }}>
            <span className="vox-pack-badge">{VISIBILITY_LABEL[state.visibility] || state.visibility}</span>
            <span className={`vox-pack-badge ${paid ? 'paid' : 'free'}`}>{paid ? formatPrice(state.price) : 'Free'}</span>
            {state.is_mine ? <span className="vox-pack-badge">Yours</span> : null}
            {state.installed ? <span className="vox-pack-badge">In your stickers</span> : null}
          </div>
        </div>
        <button type="button" aria-label="Close" onClick={onClose} disabled={!!busy} style={{ border: 0, background: 'transparent', color: 'rgba(var(--vx-fg-rgb, 255,255,255), calc(0.7 * var(--vx-ov, 1)))', cursor: 'pointer', fontSize: 16 }}>
          ✕
        </button>
      </div>

      {state.description ? <div style={{ fontSize: 13, opacity: 0.85, marginTop: 12, lineHeight: 1.45, overflowWrap: 'anywhere' }}>{state.description}</div> : null}

      {state.preview_only ? <div style={{ fontSize: 12, opacity: 0.75, marginTop: 12 }}>Preview: {(state.stickers || []).length} of {state.sticker_count} stickers. The rest are shown once you have the pack.</div> : null}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(72px, 1fr))', gap: 6, marginTop: 14, maxHeight: 260, overflowY: 'auto' }} data-testid="pack-preview">
        {(state.stickers || []).map((s) => (
          <div key={s.id} title={s.name} style={{ aspectRatio: '1', borderRadius: 8, background: 'rgba(var(--vx-fg-rgb, 255,255,255), calc(0.04 * var(--vx-ov, 1)))', padding: 4 }}>
            <img src={s.url} alt={s.name} loading="lazy" style={{ width: '100%', height: '100%', objectFit: 'contain' }} />
          </div>
        ))}
      </div>

      {err ? <div className="coms-form-error" role="alert" style={{ marginTop: 10 }}>{err}</div> : null}
      {done ? <div role="status" style={{ marginTop: 10, fontSize: 12, color: '#a7f3d0' }}>{done}</div> : null}
      {checkout ? (
        <div role="status" data-testid="payment-pending" style={{ marginTop: 10, fontSize: 12, lineHeight: 1.45, padding: 10, borderRadius: 10, background: 'rgba(245,158,11,0.12)', border: '1px solid rgba(245,158,11,0.4)' }}>
          <strong>Payment activation is pending.</strong> Buying sticker packs ({formatPrice(checkout.quote)}) opens once Voxemera activates payments. You have not been charged and nothing was bought.
        </div>
      ) : null}

      <div style={{ marginTop: 14, display: 'flex', flexDirection: 'column', gap: 10 }}>
        {state.can_install || (needsTrust && servers.some((s) => s.can_enable)) ? (
          needsTrust ? (
            <div style={{ fontSize: 12, lineHeight: 1.45, padding: 10, borderRadius: 10, background: 'rgba(245,158,11,0.12)', border: '1px solid rgba(245,158,11,0.4)' }}>
              {IMPORT_WARNING}
              <label style={{ display: 'flex', gap: 6, alignItems: 'center', marginTop: 8 }}>
                <input type="checkbox" checked={trusted} onChange={(e) => setTrusted(e.target.checked)} />I trust this creator
              </label>
            </div>
          ) : null
        ) : null}

        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
          {state.can_install ? (
            <button type="button" className="coms-mini-btn primary" disabled={!!busy || (needsTrust && !trusted)} onClick={install}>
              {busy === 'install' ? 'Adding…' : 'Add to my stickers'}
            </button>
          ) : null}
          {!state.can_install && state.install_blocked === 'purchase_required' && !state.is_mine ? (
            <button type="button" className="coms-mini-btn primary" disabled={!!busy} onClick={buy} data-testid="pack-buy">
              {busy === 'buy' ? 'Opening checkout…' : `Buy for ${formatPrice(state.price)}`}
            </button>
          ) : null}
          {!state.can_install && state.install_blocked === 'server_pack' ? <span style={{ fontSize: 12, opacity: 0.8 }}>This pack belongs to a server and is available inside it.</span> : null}
          {servers.map((s) =>
            s.enabled ? (
              <span key={s.key} className="vox-pack-badge">Enabled in {s.name}</span>
            ) : s.can_enable ? (
              <button key={s.key} type="button" className="coms-mini-btn" disabled={!!busy || (needsTrust && !trusted)} onClick={() => enable(s.key)}>
                {busy === `enable:${s.key}` ? 'Enabling…' : `Enable for ${s.name}`}
              </button>
            ) : serverKey ? (
              <span key={s.key} style={{ fontSize: 12, opacity: 0.8 }}>{s.reason || 'This pack cannot be enabled here.'}</span>
            ) : null
          )}
          <button type="button" className="coms-mini-btn" style={{ marginLeft: 'auto' }} onClick={onClose} disabled={!!busy}>
            Close
          </button>
        </div>
      </div>
    </ModalShell>
  );
}
